import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getEntitlements } from "@helpers/entitlements";
import { useAppStore } from "@hooks/useAppStore";
import useExams from "@hooks/useExams";
import { formatDate } from "@memorang/helpers/date";
import { isWeb } from "@memorang/helpers/platform";

export const useMoreSettings = () => {
	const { t } = useTranslation();
	const {
		currentExamContext: { examIcon, examName, examDate },
	} = useExamContext();

	const [showDatePicker, setShowDatePicker] = useState(false);

	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};

	const { examsList } = useExams();
	const allowChangeExam = useAppStore((store) => store.exam.allowChangeExam);
	const isTokenBasedFlow = useAppStore(
		(store) => store.app.auth?.tokenBasedFlow ?? false,
	);
	const canExtendSubscription = !isTokenBasedFlow;

	const hasMoreThanOneExam = examsList.length > 1;
	const canSwitchExam = Boolean(allowChangeExam) && hasMoreThanOneExam;

	const entitlements = getEntitlements(examsList);

	const moreSettingsSections = [
		{
			sectionTitle: t("more.profile.title"),
			data: [
				{
					icon: "account-outline" as const,
					title: t("dashboard.menu.profile"),
					rightCta: t("common.view"),
					href: "/profile",
					type: "profile" as const,
				},
			],
		},
		{
			sectionTitle: t("account.accountInfo"),
			data: [
				{
					icon: "cog-outline" as const,
					title: t("dashboard.menu.settings"),
					rightCta: t("common.view"),
					href: "/account/settings",
					type: "account-settings" as const,
				},
			],
		},
		{
			sectionTitle: t("more.exam.title"),
			data: [
				{
					thumbnail: examIcon,
					title: examName,
					rightCta: canSwitchExam ? t("common.switch") : undefined,
					type: "switch-exam" as const,
				},
				{
					icon: "calendar-outline" as const,
					type: "change-date" as const,
					title: examDate ? formatDate(examDate) : t("account.selectDate"),
					rightCta: t("common.update"),
				},
			],
		},
		...(entitlements.length > 0
			? [
					{
						sectionTitle: t("more.subscriptions.title"),
						data: [
							...(isWeb
								? [
										{
											icon: "gift-outline" as const,
											title: t("more.subscriptions.redeem.title"),
											type: "redeem" as const,
											href: "/redeem?step=redeem",
										},
									]
								: []),
							...entitlements.map(({ name, icon, id, subscription }) => {
								const endDate = subscription?.endDate;
								return {
									title: name,
									subtitle: endDate
										? t("account.validUntil", {
												date: formatDate(endDate),
											})
										: "",
									rightCta: canExtendSubscription
										? t("more.subscriptions.extend.cta")
										: undefined,
									type: "subscription" as const,
									thumbnail: icon,
									id,
								};
							}),
						],
					},
				]
			: []),
	];

	return {
		moreSettingsSections,
		toggleShowDatePicker,
		showDatePicker,
		entitlements,
		canSwitchExam,
		canExtendSubscription,
	};
};
